/**
 * ProfileSheet — display name + avatar upload.
 *
 * Avatar handling: read the user's file → draw to a canvas at 256×256 →
 * export as JPEG at 0.85 quality. Most phone selfies are 4–8 MB raw and
 * the backend caps at 256 KB, so the client-side downscale is mandatory.
 * Result is a data: URI we PATCH directly to /users/me — no S3 needed
 * for what is effectively a 3 KB string.
 */
import { useRef, useState } from "react";
import { Camera, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/chalkApi";
import { BottomSheet, ChalkActionButton } from "./primitives";

const AVATAR_SIZE = 256;
const JPEG_QUALITY = 0.85;

async function _fileToDataUri(file) {
  // Decode → draw to a centered square canvas → re-encode small.
  const blobUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => {
      const el = new Image();
      el.onload = () => res(el);
      el.onerror = rej;
      el.src = blobUrl;
    });
    const canvas = document.createElement("canvas");
    canvas.width = AVATAR_SIZE;
    canvas.height = AVATAR_SIZE;
    const ctx = canvas.getContext("2d");
    const min = Math.min(img.width, img.height);
    const sx = (img.width - min) / 2;
    const sy = (img.height - min) / 2;
    ctx.drawImage(img, sx, sy, min, min, 0, 0, AVATAR_SIZE, AVATAR_SIZE);
    return canvas.toDataURL("image/jpeg", JPEG_QUALITY);
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

export default function ProfileSheet({ isOpen, user, onDismiss, onSaved }) {
  const [displayName, setDisplayName] = useState(user?.display_name || "");
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar_url || null);
  const [pendingAvatar, setPendingAvatar] = useState(null); // dirty marker
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  async function pick(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Pick an image file.");
      return;
    }
    try {
      const dataUri = await _fileToDataUri(file);
      setAvatarPreview(dataUri);
      setPendingAvatar(dataUri);
    } catch {
      toast.error("Couldn't read that image.");
    } finally {
      e.target.value = ""; // allow re-picking the same file
    }
  }

  async function clear() {
    setAvatarPreview(null);
    setPendingAvatar("");  // empty string signals server-side clear
  }

  async function save() {
    const body = {};
    const trimmed = displayName.trim();
    const original = (user?.display_name || "").trim();
    if (trimmed !== original) body.display_name = trimmed;
    if (pendingAvatar !== null) body.avatar = pendingAvatar;
    if (Object.keys(body).length === 0) {
      onDismiss && onDismiss();
      return;
    }
    setSaving(true);
    try {
      const data = await api.updateProfile(body);
      toast.success("Profile updated.");
      onSaved && onSaved(data.user);
      onDismiss && onDismiss();
    } catch (e) {
      toast.error(e.message || "Couldn't save your profile.");
    } finally {
      setSaving(false);
    }
  }

  const initial = (displayName || user?.first_name || user?.email || "?")
    .trim()[0]
    ?.toUpperCase() || "?";

  return (
    <BottomSheet isOpen={isOpen} title="Your profile" onDismiss={onDismiss}>
      <div data-testid="profile-sheet" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button
            type="button"
            data-testid="profile-avatar-button"
            onClick={() => fileRef.current && fileRef.current.click()}
            aria-label="Change avatar"
            style={{
              position: "relative", width: 84, height: 84, borderRadius: "50%",
              background: avatarPreview ? "none"
                : `linear-gradient(135deg, ${user?.avatar_color || "#2E7D52"}, #1F5C3C)`,
              border: "0.5px solid var(--chalk-border)",
              overflow: "hidden", cursor: "pointer", padding: 0,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontFamily: "var(--font-display, 'Bebas Neue', sans-serif)",
              fontSize: 38, letterSpacing: "0.04em",
            }}
          >
            {avatarPreview ? (
              <img src={avatarPreview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : initial}
            <span aria-hidden="true" style={{
              position: "absolute", right: 4, bottom: 4,
              width: 26, height: 26, borderRadius: "50%",
              background: "var(--chalk-board)", color: "#fff",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              border: "2px solid #FBF8F2",
            }}>
              <Camera size={13} />
            </span>
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={pick}
            data-testid="profile-avatar-input"
            style={{ display: "none" }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: "var(--chalk-board)", fontWeight: 600 }}>
              {displayName.trim() || `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.email}
            </div>
            <div style={{ fontSize: 11, color: "var(--chalk-muted)", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {user?.email}
            </div>
            {avatarPreview && (
              <button
                type="button"
                onClick={clear}
                data-testid="profile-avatar-clear"
                style={{
                  marginTop: 6, padding: "4px 8px",
                  fontSize: 11, fontWeight: 600,
                  background: "transparent", border: "0.5px solid var(--chalk-border)",
                  color: "var(--chalk-muted)", borderRadius: 6, cursor: "pointer",
                  display: "inline-flex", alignItems: "center", gap: 4,
                }}
              >
                <Trash2 size={11} aria-hidden="true" /> Remove photo
              </button>
            )}
          </div>
        </div>

        <label style={{ display: "block" }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--chalk-muted)", marginBottom: 6, fontWeight: 700 }}>
            Display name
          </div>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Coach Kyto"
            maxLength={40}
            autoCapitalize="words"
            data-testid="profile-display-name"
            style={{
              width: "100%", border: "0.5px solid var(--chalk-border)",
              borderRadius: 10, padding: "11px 12px", fontSize: 16,
              fontFamily: "inherit", outline: "none",
              background: "#FBF8F2", color: "var(--chalk-board)",
            }}
          />
          <div style={{ fontSize: 11, color: "var(--chalk-muted)", marginTop: 6, lineHeight: 1.4 }}>
            This is what teammates and families see — your email stays private.
          </div>
        </label>

        <ChalkActionButton
          testId="profile-save"
          variant="primary"
          label={saving ? "Saving…" : "Save profile"}
          icon={<Save size={16} aria-hidden="true" />}
          loading={saving}
          onClick={save}
        />
      </div>
    </BottomSheet>
  );
}
